import { useSyncExternalStore } from "react";

/**
 * Tiny, dependency-free global store for the "minimized navigation" feature.
 * The active navigation page (NavigatePage) publishes its current turn-by-turn
 * state into this store; a globally-mounted banner subscribes and renders a
 * compact pill at the top of every page so the user can keep using the app
 * while navigation runs in the background.
 */

export interface MinimizedNavState {
  /** True when /navigate page is mounted and the user has minimized it. */
  active: boolean;
  /** Trip ID the user is navigating — used to re-open the page on tap. */
  tripId: string | null;
  /** Maneuver glyph from tomtomRoute (🡅, ⮕, …). */
  maneuverIcon: string | null;
  /** Short maneuver label. */
  maneuverLabel: string | null;
  /** Pre-formatted distance to next maneuver, e.g. "450 m". */
  distanceLabel: string | null;
  /** Optional street name. */
  street: string | null;
  /** Optional friendly name of the next stop ("Kronocamping"). */
  nextStopName: string | null;
  /** True when navigation is paused — banner shows pause indicator. */
  paused: boolean;
}

const INITIAL: MinimizedNavState = {
  active: false,
  tripId: null,
  maneuverIcon: null,
  maneuverLabel: null,
  distanceLabel: null,
  street: null,
  nextStopName: null,
  paused: false,
};

let state: MinimizedNavState = INITIAL;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export const navigationMinimizeStore = {
  getState: () => state,
  subscribe: (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Set/update minimized state. Pass `{active: false}` to hide banner. */
  set: (next: Partial<MinimizedNavState>) => {
    state = { ...state, ...next };
    emit();
  },
  /** Convenience: fully clear (called when user exits navigation). */
  clear: () => {
    state = INITIAL;
    emit();
  },
};

export function useMinimizedNav(): MinimizedNavState {
  return useSyncExternalStore(
    navigationMinimizeStore.subscribe,
    navigationMinimizeStore.getState,
    () => INITIAL,
  );
}
